import React, { useEffect, useState } from 'react';
import { XIcon, Trash2Icon, MinusIcon, PlusIcon } from 'lucide-react';
interface OrderItem {
  id: string;
  quantity: number;
  hasEngraving?: boolean;
  engravingMessage?: string;
}
interface Product {
  id: string;
  name: string;
  price: string;
  bulkPrice?: string;
  engravingPrice?: string;
  bulkMinimum?: number;
  images: string[];
}
interface OrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (name: string, phone: string, engravingMessages: Record<string, string>) => void;
  orderItems: OrderItem[];
  products: Product[];
  onRemoveItem: (id: string, hasEngraving?: boolean) => void;
  onUpdateQuantity: (id: string, quantity: number, hasEngraving?: boolean) => void;
}
const OrderModal: React.FC<OrderModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  orderItems,
  products,
  onRemoveItem,
  onUpdateQuantity
}) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [nameError, setNameError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [step, setStep] = useState<'review' | 'contact'>('review');
  const [engravingMessages, setEngravingMessages] = useState<Record<string, string>>({});
  const getProductDetails = (id: string) => {
    return products.find(product => product.id === id);
  };
  const calculateItemPrice = (item: OrderItem) => {
    const product = getProductDetails(item.id);
    if (!product) return '0.00';
    const isBulkOrder = product.bulkMinimum && item.quantity >= product.bulkMinimum;
    const basePrice = isBulkOrder && product.bulkPrice ? parseFloat(product.bulkPrice.replace('$', '')) : parseFloat(product.price.replace('$', ''));
    // Add engraving cost if applicable (only for non-bulk orders)
    const engravingCost = !isBulkOrder && item.hasEngraving && product.engravingPrice ? parseFloat(product.engravingPrice.replace('$', '')) : 0;
    return ((basePrice + engravingCost) * item.quantity).toFixed(2);
  };
  const calculateTotal = () => {
    return orderItems.reduce((total, item) => {
      return total + parseFloat(calculateItemPrice(item));
    }, 0).toFixed(2);
  };
  // Function to validate Australian phone number
  const isValidAustralianPhoneNumber = (phoneNumber: string) => {
    // Remove all non-digit characters
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    // Check if it's exactly 10 digits (Australian standard)
    return digitsOnly.length === 10;
  };
  // Initialize engravingMessages from orderItems when modal opens
  useEffect(() => {
    if (isOpen) {
      const messages: Record<string, string> = {};
      orderItems.forEach(item => {
        if (item.hasEngraving && item.engravingMessage) {
          messages[item.id] = item.engravingMessage;
        }
      });
      setEngravingMessages(messages);
    }
  }, [isOpen, orderItems]);
  if (!isOpen) return null;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Reset errors
    setNameError('');
    setPhoneError('');
    // Validate
    let isValid = true;
    if (!name.trim()) {
      setNameError('Please enter your name');
      isValid = false;
    }
    if (!phone.trim()) {
      setPhoneError('Please enter your phone number');
      isValid = false;
    } else if (!isValidAustralianPhoneNumber(phone)) {
      setPhoneError('Please enter a valid Australian phone number (10 digits)');
      isValid = false;
    }
    if (isValid) {
      onConfirm(name, phone, engravingMessages);
    }
  };
  const handleContinue = () => {
    if (orderItems.length === 0) {
      onClose();
      return;
    }
    setStep('contact');
  };
  const handleEngravingMessageChange = (itemId: string, hasEngraving: boolean | undefined, message: string) => {
    if (hasEngraving) {
      setEngravingMessages({
        ...engravingMessages,
        [itemId]: message
      });
    }
  };
  // Format phone number as user types
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    // Remove any non-digit characters
    const digitsOnly = input.replace(/\D/g, '');
    // Limit to 10 digits
    const truncated = digitsOnly.slice(0, 10);
    // Format as XXX XXX XXX
    let formatted = truncated;
    if (truncated.length > 3) {
      formatted = `${truncated.slice(0, 4)} ${truncated.slice(4)}`;
    }
    if (truncated.length > 6) {
      formatted = `${truncated.slice(0, 4)} ${truncated.slice(4, 7)} ${truncated.slice(7)}`;
    }
    setPhone(formatted);
  };
  return <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-19">
      <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0" data-id="element-20">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} data-id="element-21"></div>
        <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true" data-id="element-22">
          &#8203;
        </span>
        <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle" data-id="element-23">
          <div className="absolute right-0 top-0 pr-4 pt-4" data-id="element-24">
            <button type="button" className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none" onClick={onClose} data-id="element-25">
              <span className="sr-only" data-id="element-26">Close</span>
              <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-27" />
            </button>
          </div>
          {step === 'review' ? <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-28">
              <div className="sm:flex sm:items-start" data-id="element-29">
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full" data-id="element-30">
                  <h3 className="text-xl font-medium leading-6 text-gray-900 mb-4" data-id="element-31">
                    Your Order
                  </h3>
                  {orderItems.length === 0 ? <div className="py-8 text-center text-gray-500" data-id="element-32">
                      Your cart is empty
                    </div> : <div className="max-h-[50vh] overflow-y-auto pr-2" data-id="element-33">
                      <ul className="divide-y divide-gray-200" data-id="element-34">
                        {orderItems.map((item, index) => {
                    const product = getProductDetails(item.id);
                    if (!product) return null;
                    const isBulkOrder = product.bulkMinimum && item.quantity >= product.bulkMinimum;
                    const itemPrice = calculateItemPrice(item);
                    return <li key={`${item.id}-${item.hasEngraving ? 'engraved' : 'standard'}-${index}`} className="py-4" data-id="element-35">
                              <div className="flex items-start" data-id="element-36">
                                <div className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-md border border-gray-200" data-id="element-37">
                                  <img src={product.images[0]} alt={product.name} className="h-full w-full object-cover object-center" data-id="element-38" />
                                </div>
                                <div className="ml-4 flex-1" data-id="element-39">
                                  <div className="flex justify-between" data-id="element-40">
                                    <h4 className="text-sm font-medium text-gray-900" data-id="element-41">
                                      {product.name}
                                    </h4>
                                    <p className="ml-4 text-sm font-medium text-gray-900" data-id="element-42">
                                      ${itemPrice}
                                    </p>
                                  </div>
                                  <p className="mt-1 text-sm text-gray-500" data-id="element-43">
                                    {isBulkOrder ? product.bulkPrice : product.price}{' '}
                                    each
                                  </p>
                                  {item.hasEngraving && <>
                                      <p className="mt-1 text-xs text-gray-500" data-id="element-44">
                                        With personal engraving{' '}
                                        {isBulkOrder ? '(Free)' : `(+${product.engravingPrice} each)`}
                                      </p>
                                      <div className="mt-1" data-id="element-45">
                                        <p className="text-xs text-gray-700 p-1.5 bg-gray-50 border border-gray-200 rounded" data-id="element-46">
                                          {item.engravingMessage || engravingMessages[item.id] ? `"${item.engravingMessage || engravingMessages[item.id]}"` : 'No personal message set'}
                                        </p>
                                      </div>
                                    </>}
                                  <div className="mt-2 flex items-center justify-between" data-id="element-47">
                                    <div className="flex items-center border border-gray-300 rounded" data-id="element-48">
                                      <button onClick={() => {
                                if (item.quantity > 1) {
                                  onUpdateQuantity(item.id, item.quantity - 1, item.hasEngraving);
                                }
                              }} className="px-2 py-1 text-gray-600 hover:bg-gray-100" data-id="element-49">
                                        <MinusIcon className="h-4 w-4" data-id="element-50" />
                                      </button>
                                      <span className="px-2 py-1 text-sm" data-id="element-51">
                                        {item.quantity}
                                      </span>
                                      <button onClick={() => onUpdateQuantity(item.id, item.quantity + 1, item.hasEngraving)} className="px-2 py-1 text-gray-600 hover:bg-gray-100" data-id="element-52">
                                        <PlusIcon className="h-4 w-4" data-id="element-53" />
                                      </button>
                                    </div>
                                    <button onClick={() => onRemoveItem(item.id, item.hasEngraving)} className="text-sm text-red-600 hover:text-red-500 flex items-center" data-id="element-54">
                                      <Trash2Icon className="h-4 w-4 mr-1" data-id="element-55" />
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </li>;
                  })}
                      </ul>
                    </div>}
                  {orderItems.length > 0 && <div className="mt-6 border-t border-gray-200 pt-4" data-id="element-56">
                      <div className="flex justify-between text-base font-medium text-gray-900" data-id="element-57">
                        <p data-id="element-58">Total</p>
                        <p data-id="element-59">${calculateTotal()}</p>
                      </div>
                      <p className="mt-0.5 text-sm text-gray-500" data-id="element-60">
                        Shipping and taxes calculated at checkout.
                      </p>
                    </div>}
                </div>
              </div>
            </div> : <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-61">
              <div className="sm:flex sm:items-start" data-id="element-62">
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full" data-id="element-63">
                  <h3 className="text-xl font-medium leading-6 text-gray-900 mb-6" data-id="element-64">
                    Contact Information
                  </h3>
                  <form onSubmit={handleSubmit} className="space-y-4" data-id="element-65">
                    <div data-id="element-66">
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-67">
                        Your Name
                      </label>
                      <input type="text" id="name" value={name} onChange={e => setName(e.target.value)} className={`mt-1 block w-full rounded-md border ${nameError ? 'border-red-300' : 'border-gray-300'} px-3 py-2 shadow-sm focus:border-[#d4b8c7] focus:outline-none focus:ring-[#d4b8c7]`} placeholder="Enter your full name" data-id="element-68" />
                      {nameError && <p className="mt-1 text-sm text-red-600" data-id="element-69">{nameError}</p>}
                    </div>
                    <div data-id="element-70">
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700" data-id="element-71">
                        Australian Phone Number
                      </label>
                      <input type="tel" id="phone" value={phone} onChange={handlePhoneChange} className={`mt-1 block w-full rounded-md border ${phoneError ? 'border-red-300' : 'border-gray-300'} px-3 py-2 shadow-sm focus:border-[#d4b8c7] focus:outline-none focus:ring-[#d4b8c7]`} placeholder="e.g. 0400 123 456" data-id="element-72" />
                      {phoneError && <p className="mt-1 text-sm text-red-600" data-id="element-73">
                          {phoneError}
                        </p>}
                      <p className="mt-1 text-xs text-gray-500" data-id="element-74">
                        Please enter a 10-digit Australian phone number
                      </p>
                    </div>
                    <p className="text-sm text-gray-500 mt-2" data-id="element-75">
                      A member of our team will call you to confirm your order
                      details.
                    </p>
                  </form>
                </div>
              </div>
            </div>}
          <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6" data-id="element-76">
            {step === 'review' ? <>
                <button type="button" className="inline-flex w-full justify-center rounded-md bg-[#752F50] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#c6a6b7] sm:ml-3 sm:w-auto disabled:bg-gray-300 disabled:cursor-not-allowed" onClick={handleContinue} disabled={orderItems.length === 0} data-id="element-77">
                  {orderItems.length === 0 ? 'Close' : 'Continue to Checkout'}
                </button>
                <button type="button" className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto" onClick={onClose} data-id="element-78">
                  Continue Shopping
                </button>
              </> : <>
                <button type="button" className="inline-flex w-full justify-center rounded-md bg-[#752F50] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#c6a6b7] sm:ml-3 sm:w-auto" onClick={handleSubmit} data-id="element-79">
                  Confirm Order
                </button>
                <button type="button" className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto" onClick={() => setStep('review')} data-id="element-80">
                  Back
                </button>
              </>}
          </div>
        </div>
      </div>
    </div>;
};
export default OrderModal;